<template>
  <div
    v-if="smsActive"
    data-cy="sms-messages"
    class="absolute bottom-4 z-[101] flex flex-col"
    :class="
      collapsedView
        ? 'right-2 pr-2'
        : 'right-0 max-h-screen gap-y-2 overflow-y-auto pt-8 pr-4 pb-4 pl-[66px]'
    "
  >
    <div
      v-if="!collapsedView"
      class="pointer-events-none fixed top-0 right-4 z-300 h-6 w-[320px] bg-linear-to-t from-transparent to-white/50"
    />
    <show-less-and-close
      v-if="!collapsedView"
      @collapse="collapsedView = true"
      @close="closeSms"
    />
    <sms-card
      :sms="latestIncomingSms"
      is-top-level
      :is-collapsed="collapsedView"
      @collapsed:toggle="toggleCollapsed"
      @close="closeSms"
      @open="openConversation"
    />
    <sms-card
      v-for="(sms, index) in additionalSmsMessages"
      :key="sms.sms_log_id"
      :sms="sms"
      :index="index + 1"
      :is-collapsed="collapsedView"
      @open="openConversation"
    />
    <show-less-and-close
      v-if="!collapsedView && additionalSmsMessages.length > 5"
      @collapse="collapsedView = true"
      @close="closeSms"
    />
    <div
      v-if="!collapsedView"
      class="pointer-events-none fixed right-4 bottom-4 z-300 h-6 w-[320px] bg-linear-to-b from-transparent to-white/50"
    />
  </div>
  <sms-conversation
    v-if="conversationActive && activeSmsConversation"
    :incoming-sms-events="incomingSmsEvents"
    :incoming-event="incomingSmsEventData"
    @close="closeConversation"
  />
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";

import { storeToRefs } from "pinia";

import {
  leaveChannel,
  subscribeToChannelEvents,
} from "@/helpers/websockets/channels";
import { USER_SMS_CHANNEL } from "@/helpers/websockets/common";
import { useAuth } from "@/hooks/auth";
import useSmsStore from "@/stores/sms";

import SmsConversation from "@/components/sms/Conversation.vue";
import ShowLessAndClose from "@/components/twilio/ShowLessAndClose.vue";
import SmsCard from "@/components/twilio/SmsCard.vue";

const STACKED_CARD_LIMIT = 3;

const { incomingSmsEvents, incomingSmsEventData, activeSmsConversation } =
  storeToRefs(useSmsStore());
const { currentUser } = useAuth();
const smsActive = ref(false);
const conversationActive = ref(false);
const collapsedView = ref(true);

const latestIncomingSms = computed(() => incomingSmsEvents.value?.[0] ?? null);
const additionalSmsMessages = computed(() =>
  (incomingSmsEvents.value ?? []).slice(
    1,
    collapsedView.value ? STACKED_CARD_LIMIT : undefined
  )
);

const closeSms = () => {
  incomingSmsEvents.value = [];
  smsActive.value = false;
};

const toggleCollapsed = () => {
  if (incomingSmsEvents.value?.length <= 1) {
    return;
  }
  collapsedView.value = !collapsedView.value;
};

const openConversation = async (id: number, logId: number) => {
  if (!id) {
    return;
  }
  const indexOfConversation = incomingSmsEvents.value.findIndex(
    (sms) => sms.sms_conversation_id === id && sms.sms_log_id === logId
  );
  activeSmsConversation.value = incomingSmsEvents.value[indexOfConversation];
  if (indexOfConversation > -1) {
    incomingSmsEvents.value.splice(indexOfConversation, 1);
    conversationActive.value = true;
  } else {
    conversationActive.value = false;
  }
};

const closeConversation = () => {
  activeSmsConversation.value = null;
  conversationActive.value = false;
};

watch(
  () => incomingSmsEvents.value.length,
  (newValue) => {
    smsActive.value = !!newValue;
  }
);

onMounted(() => {
  if (!currentUser) {
    return;
  }
  subscribeToChannelEvents(USER_SMS_CHANNEL, String(currentUser.id));
});

onBeforeUnmount(() => {
  if (!currentUser) {
    return;
  }
  leaveChannel(USER_SMS_CHANNEL, String(currentUser.id));
});
</script>
